import type { CompanyRole, DeptRole } from '@/features/permissions/domain/permission'

type Credentials = {
	email: string
	password: string
}

type AuthError = {
	message: string
}

type User = {
	id: string
	email: string
	fullName: string | null
	avatarUrl: string | null
	onboarded: boolean
	companyRole: CompanyRole
	deptRole: DeptRole
	departmentId: string | null
	teamIds: string[]
}

type Session = {
	user: User
}

type AuthResult = { success: true; session: Session } | { success: false; error: AuthError }

type AuthRedirect = '/' | '/login' | '/onboarding'

// Pure guard logic shared by the route guards and the post-login navigation.
// Returns the path the user belongs on, or null when they may stay put.
const resolveAuthRedirect = (session: Session | null, current: AuthRedirect): AuthRedirect | null => {
	if (!session) return current === '/login' ? null : '/login'
	if (!session.user.onboarded) return current === '/onboarding' ? null : '/onboarding'
	if (current === '/login' || current === '/onboarding') return '/'
	return null
}

type AuthPort = {
	login(credentials: Credentials): Promise<AuthResult>
	logout(): Promise<void>
}

type SessionPort = {
	get(): Session | null
	set(session: Session | null): void
	patchUser(patch: Partial<User>): void
}

type UserProfilePort = {
	updateName(userId: string, name: string): Promise<void>
	setupOrg(companyName: string): Promise<void>
}

export { resolveAuthRedirect }
export type { Credentials, AuthError, AuthResult, AuthPort, SessionPort, User, Session, UserProfilePort, AuthRedirect }
